import React, { useRef, useEffect, useCallback, memo, useMemo, useState } from "react";
import { useMedia } from "@rapid/contexts/MediaContext";
import { useFlow } from "@rapid/contexts/FlowContext";
import { BuildSteps, DeploySteps, Flows } from "@rapid/types/flows";
import { useFps } from "@rapid/contexts/FpsContext";
import { useVideoSegmentation } from "@rapid/utils/videoSegmentation";
import { useFilePreview } from "@rapid/contexts/FilePreviewContext";
import { InferenceProgressHint } from "@rapid/components/flow/build/Hints/InferenceProgressHint";
import { DownloadingHint } from "@rapid/components/flow/build/Hints/DownloadingHint";
import { createPortal } from "react-dom";
import { useGetFileCarouselHintPortalElement } from "../useCarousel";
import { AnnotateHint } from "@rapid/components/flow/build/Hints/AnnotateHint";
import { BackspaceHint } from "@rapid/components/flow/build/Hints/BackspaceHint";
import { useFlowState } from "@rapid/hooks/useFlowState";
import {
    useCurrentMedia,
    useCurrentMediaFileVideoMetadata,
    useCurrentMediaFileKeyframeOffset,
    useMediaStore
} from "@rapid/store";
import { noop } from "lodash";
import { useIsGeneratingPredictions } from "@rapid/hooks/useIsGeneratingPredictions";
import { useMediaUtils } from "@rapid/hooks/useMediaUtils";
import { FileInputDimensions } from "@rapid/types/media";

// Type definition for VideoFrameCallbackMetadata
declare global {
    interface VideoFrameCallbackMetadata {
        presentationTime: DOMHighResTimeStamp;
        expectedDisplayTime: DOMHighResTimeStamp;
        width: number;
        height: number;
        mediaTime: number;
        presentedFrames: number;
        processingDuration?: number;
        captureTime?: DOMHighResTimeStamp;
        receiveTime?: DOMHighResTimeStamp;
        rtpTimestamp?: number;
    }

    interface HTMLVideoElement {
        requestVideoFrameCallback(
            callback: (now: DOMHighResTimeStamp, metadata: VideoFrameCallbackMetadata) => void
        ): number;
        cancelVideoFrameCallback(handle: number): void;
    }
}

interface VideoFilePreviewProps {
    forwardedRef?: React.Ref<HTMLVideoElement>;
    onMediaReady?: () => void;
    effectiveZoomLevel: number;
    effectiveZoomX: number;
    effectiveZoomY: number;
    // Shared state props
    localNaturalDimensions: FileInputDimensions;
    setLocalNaturalDimensions: (dimensions: FileInputDimensions) => void;
    localRenderedDimensions: FileInputDimensions;
    setLocalRenderedDimensions: (dimensions: FileInputDimensions) => void;
    // States that parent needs to know about
    fileUrl: string;
    setAreFramePreviewsReady: (ready: boolean) => void;
    isPlaying: boolean;
    setIsPlaying: (playing: boolean) => void;
    // Parent refs
}

const VideoFilePreview = memo<VideoFilePreviewProps>(
    ({
        forwardedRef,
        fileUrl,
        onMediaReady,
        effectiveZoomLevel,
        effectiveZoomX,
        effectiveZoomY,
        localNaturalDimensions,
        setLocalNaturalDimensions,
        localRenderedDimensions,
        setLocalRenderedDimensions,
        setAreFramePreviewsReady,
        isPlaying,
        setIsPlaying
    }) => {
        const videoRef = useRef<HTMLVideoElement>(null);
        const [videoMetadataLoaded, setVideoMetadataLoaded] = useState(false);
        const frameCallbackIdRef = useRef<number | null>(null);

        const { step, flow } = useFlowState();
        const { flowLocked } = useFlow();
        const fileCarouselHintPortalElement = useGetFileCarouselHintPortalElement();
        const { setVideoControls, onVideoPause, initializedVideosRef } = useFilePreview();
        const { storageUploadDir } = useMedia();

        const media = useMediaStore();
        const currentMedia = useCurrentMedia();
        const currentMediaFileVideoMetadata = useCurrentMediaFileVideoMetadata();
        const currentMediaFileKeyframeOffset = useCurrentMediaFileKeyframeOffset();
        const { extractFrameFileFromMediaItem, uploadMediaItem, setCurrentMedia } = useMediaUtils();

        const isReviewStep = step === BuildSteps.REVIEW;

        const isVideo = useMemo(
            () => currentMedia?.file.isVideo ?? false,
            [currentMedia?.file.isVideo]
        );

        const duration = useMemo(
            () => currentMediaFileVideoMetadata?.duration || 0,
            [currentMediaFileVideoMetadata?.duration]
        );

        // When in review mode, use the dynamic FPS that was used for inference
        // Otherwise, use FPS from video metadata for consistency with stored frame_count
        const { fps: inferenceFps } = useFps();
        const videoFps = inferenceFps || currentMediaFileVideoMetadata?.fps || 30;

        const { clampedTimeForSegment, segmentForTargetTime } = useVideoSegmentation(
            duration,
            videoFps
        );

        useEffect(() => {
            if (!isVideo) return;
            const videoElement = videoRef.current;
            if (!videoElement) return;

            if (
                !isPlaying &&
                currentMedia?.file?.id &&
                !initializedVideosRef.current.includes(currentMedia?.file.id)
            ) {
                if (currentMedia.file.frame0Id) {
                    initializedVideosRef.current.push(currentMedia.file.id);
                }
            }
        }, [
            isVideo,
            isPlaying,
            initializedVideosRef,
            videoRef,
            currentMedia?.file?.id,
            currentMedia?.file?.frame0Id
        ]);

        const handleClockTick = useCallback(
            (currentTime: number, mediaItemId: string) => {
                if (mediaItemId !== currentMedia?.file.id) return;

                const correctedTime = currentTime - (currentMediaFileKeyframeOffset || 0);
                const segment = segmentForTargetTime(correctedTime);
                const segmentFrameId = currentMedia?.file?.segmentToFrameMediaItemIdMap?.[segment];
                const frameMediaItem = segmentFrameId ? media[segmentFrameId] : undefined;

                // Use flushSync to force immediate synchronous update
                if (frameMediaItem) {
                    setCurrentMedia({
                        file: currentMedia?.file,
                        frameSegment: frameMediaItem.metadata?.segment!,
                        frame: frameMediaItem
                    });
                } else if (isReviewStep || isPlaying) {
                    // For predictions, we don't have a frame but still need to set the segment
                    setCurrentMedia({
                        file: currentMedia?.file,
                        frameSegment: segment,
                        frame: undefined
                    });
                }
            },
            [
                currentMedia?.file,
                currentMediaFileKeyframeOffset,
                setCurrentMedia,
                segmentForTargetTime,
                isReviewStep,
                isPlaying,
                media
            ]
        );

        const updateCurrentFrame = useCallback(
            async (targetTime: number) => {
                const videoElement = videoRef.current;
                if (!videoElement) {
                    console.warn(
                        "[VIDEO FILE PREVIEW] 🎬 [UPDATE CURRENT FRAME ID] No video element found"
                    );
                    return;
                }

                if (!currentMedia?.file) {
                    console.warn(
                        "[VIDEO FILE PREVIEW] 🎬 [UPDATE CURRENT FRAME ID] No current media file found"
                    );
                    return;
                }

                let frameMediaItem = await extractFrameFileFromMediaItem({
                    mediaItem: currentMedia.file,
                    targetTime
                });

                if (!frameMediaItem) {
                    console.warn(
                        "[VIDEO FILE PREVIEW] 🎬 [UPDATE CURRENT FRAME ID] No frame extracted"
                    );
                    return;
                }

                uploadMediaItem(frameMediaItem, { storageUploadDir });

                const clampedTime = frameMediaItem.metadata?.clamped_time;
                const segment = frameMediaItem.metadata?.segment;

                if (!clampedTime) {
                    console.warn(
                        "[VIDEO FILE PREVIEW] 🎬 [UPDATE CURRENT FRAME ID] No clamped time found"
                    );
                    return;
                }

                setCurrentMedia({
                    file: currentMedia?.file,
                    frameSegment: segment!,
                    frame: frameMediaItem
                });

                // Only update video time if there's a meaningful difference to prevent infinite seeking
                if (
                    videoRef.current &&
                    Math.abs(videoRef.current.currentTime - clampedTime) > 0.01
                ) {
                    videoRef.current.currentTime = clampedTime;
                }

                return frameMediaItem;
            },
            [
                currentMedia?.file,
                extractFrameFileFromMediaItem,
                uploadMediaItem,
                setCurrentMedia,
                storageUploadDir
            ]
        );

        // Callback to handle timeline clicks from VideoControls
        const handleTimelineClick = useCallback(
            async (targetTime: number) => {
                await updateCurrentFrame(targetTime);
            },
            [updateCurrentFrame]
        );

        // Forward ref setup
        // 1. Forward videoRef to parent
        // 2. Setup animation frame callback to update the video element's dimensions
        useEffect(() => {
            if (forwardedRef) {
                // 1. Forward videoRef to parent
                if (typeof forwardedRef === "function") {
                    forwardedRef(videoRef.current);
                } else {
                    (forwardedRef as React.MutableRefObject<HTMLVideoElement | null>).current =
                        videoRef.current;
                }

                // 2. Setup animation frame callback to update the video element's dimensions
                if (videoRef.current) {
                    onMediaReady?.();
                    const videoEl = videoRef.current;
                    requestAnimationFrame(() => {
                        if (videoEl) {
                            const newDims = {
                                width: videoEl.clientWidth,
                                height: videoEl.clientHeight
                            };
                            if (
                                localRenderedDimensions?.width !== newDims.width ||
                                localRenderedDimensions?.height !== newDims.height
                            ) {
                                setLocalRenderedDimensions(newDims);
                            }
                        }
                    });
                }
            }
        }, [forwardedRef, onMediaReady, localRenderedDimensions, setLocalRenderedDimensions]);

        // Effect to handle window resize and recalculate video dimensions
        const handleWindowResize = useCallback(() => {
            const videoElement = videoRef.current;
            if (videoElement) {
                requestAnimationFrame(() => {
                    const newDims = {
                        width: videoElement.clientWidth,
                        height: videoElement.clientHeight
                    };
                    if (
                        localRenderedDimensions?.width !== newDims.width ||
                        localRenderedDimensions?.height !== newDims.height
                    ) {
                        setLocalRenderedDimensions(newDims);
                    }
                });
            }
        }, [localRenderedDimensions, setLocalRenderedDimensions]);

        useEffect(() => {
            window.addEventListener("resize", handleWindowResize);
            return () => window.removeEventListener("resize", handleWindowResize);
        }, [handleWindowResize]);

        // Frame-synchronized updates using requestVideoFrameCallback
        // Only updates when the video segment actually changes (not every frame)
        useEffect(() => {
            const videoElement = videoRef.current;
            if (!videoElement || !videoMetadataLoaded || !isPlaying) return;
            if (!currentMedia?.file?.id) return;

            const supportsFrameCallback = "requestVideoFrameCallback" in videoElement;
            let lastSegment: number | null = null;
            let rafId: number | null = null;

            const checkSegmentChange = (currentTime: number, mediaItemId: string) => {
                const correctedTime = currentTime - (currentMediaFileKeyframeOffset || 0);
                const currentSegment = segmentForTargetTime(correctedTime);

                // Only trigger update if segment changed
                if (currentSegment !== lastSegment) {
                    lastSegment = currentSegment;
                    handleClockTick(currentTime, mediaItemId);
                }
            };

            if (supportsFrameCallback) {
                const updateFrame = (now: number, metadata: VideoFrameCallbackMetadata) => {
                    if (!videoElement || videoElement.paused) return;

                    const mediaItemId = currentMedia?.file?.id;
                    if (mediaItemId) {
                        checkSegmentChange(metadata.mediaTime, mediaItemId);
                    }

                    frameCallbackIdRef.current =
                        videoElement.requestVideoFrameCallback(updateFrame);
                };

                frameCallbackIdRef.current = videoElement.requestVideoFrameCallback(updateFrame);

                return () => {
                    if (frameCallbackIdRef.current !== null) {
                        videoElement.cancelVideoFrameCallback(frameCallbackIdRef.current);
                        frameCallbackIdRef.current = null;
                    }
                };
            } else {
                // Fallback for browsers without requestVideoFrameCallback (Firefox, older Safari)
                const updateFrameFallback = () => {
                    if (!videoElement || videoElement.paused) {
                        if (rafId) cancelAnimationFrame(rafId);
                        return;
                    }

                    const mediaItemId = currentMedia?.file?.id;
                    if (mediaItemId) {
                        checkSegmentChange(videoElement.currentTime, mediaItemId);
                    }

                    rafId = requestAnimationFrame(updateFrameFallback);
                };

                rafId = requestAnimationFrame(updateFrameFallback);

                return () => {
                    if (rafId) cancelAnimationFrame(rafId);
                };
            }
        }, [
            isPlaying,
            videoMetadataLoaded,
            currentMedia?.file?.id,
            handleClockTick,
            currentMediaFileKeyframeOffset,
            segmentForTargetTime
        ]);

        // Video playback event handlers
        useEffect(() => {
            const videoElement = videoRef.current;

            const handlePlay = () => {
                setIsPlaying(true);
            };

            const handlePause = async () => {
                const videoElement = videoRef.current;
                const currentTime = videoElement?.currentTime;

                setIsPlaying(false);

                if (!currentTime) {
                    console.warn("[VIDEO FILE PREVIEW] 🎬 [handlePause] No current time found");
                    return;
                }

                // Extract and upload frame at pause position to enable auto-predictions
                await updateCurrentFrame(currentTime);
            };

            const handleEnded = async () => {
                const videoElement = videoRef.current;

                // For mobile Safari, ensure proper looping behavior
                if (videoElement) {
                    // Video should automatically loop due to loop={true} attribute
                    // but if it doesn't, we can force it:
                    if (!videoElement.loop) {
                        videoElement
                            .play()
                            .catch((e) => console.error("Error restarting video:", e));
                    }
                }
            };

            if (videoElement && videoMetadataLoaded) {
                // Initialize isPlaying state to match actual video state
                const actualIsPlaying = !videoElement.paused;
                if (isPlaying !== actualIsPlaying) {
                    setIsPlaying(actualIsPlaying);
                }

                const eventListeners: [keyof HTMLVideoElementEventMap, () => void][] = [
                    ["play", handlePlay],
                    ["pause", handlePause],
                    ["ended", handleEnded]
                ];

                eventListeners.forEach(([event, handler]) => {
                    videoElement.addEventListener(event, handler);
                });

                return () => {
                    eventListeners.forEach(([event, handler]) => {
                        videoElement?.removeEventListener(event, handler);
                    });
                };
            }
        }, [
            videoRef,
            videoMetadataLoaded,
            setIsPlaying,
            isPlaying,
            updateCurrentFrame,
            currentMedia?.file?.id
        ]);

        const videoSeekOnCurrentFrameIdChange = useCallback(async () => {
            if (!isVideo) return;

            const videoElement = videoRef.current;
            if (!videoElement) return;

            if (currentMedia?.frameSegment === undefined) return;

            const clampedTime = clampedTimeForSegment(currentMedia?.frameSegment);

            if (Math.abs(videoElement.currentTime - clampedTime) > 0.01) {
                // seek to the correct frame only if there's a meaningful difference (> 10ms)
                videoElement.currentTime = clampedTime;
            } else if (videoElement.currentTime === (currentMediaFileKeyframeOffset || 0)) {
                // NOTE: currently, we rely on video seeks to set the current frame state
                // there is an edge case when we click a thumbnail for a new video that
                // is on the first frame and the video does not seek because it is already
                // at the correct time. this quick seek will ensure we set the frame when
                // we seek back to the video start time (keyframe offset for trims, or 0 for non-trims)
                const originalTime = videoElement.currentTime;
                videoElement.currentTime = 0.01;
                await new Promise((resolve) => {
                    videoElement.addEventListener("seeked", resolve, { once: true });
                });
                videoElement.currentTime = originalTime;
            }
        }, [
            isVideo,
            currentMedia?.frameSegment,
            clampedTimeForSegment,
            currentMediaFileKeyframeOffset
        ]);

        useEffect(() => {
            // don't trigger a seek if the frame id is changing b/c video is playing
            if (currentMedia?.frame?.id && !isPlaying) {
                videoSeekOnCurrentFrameIdChange();
            }
        }, [isPlaying, currentMedia?.frame?.id, videoSeekOnCurrentFrameIdChange]);

        const handleVisibleVideoMetadataLoaded = useCallback(async () => {
            setVideoMetadataLoaded(true);
            const video = videoRef.current;

            if (video && video.videoWidth > 0 && video.videoHeight > 0) {
                const newDims = { width: video.videoWidth, height: video.videoHeight };
                if (
                    localNaturalDimensions?.width !== newDims.width ||
                    localNaturalDimensions?.height !== newDims.height
                ) {
                    setLocalNaturalDimensions(newDims);
                }

                // Also capture the rendered (CSS) dimensions once metadata is loaded
                // so downstream positioning (e.g., AnnotationLabelInput) has a reliable base size.
                requestAnimationFrame(() => {
                    const el = videoRef.current;
                    if (!el) return;
                    const renderedDims = { width: el.clientWidth, height: el.clientHeight };
                    if (
                        localRenderedDimensions?.width !== renderedDims.width ||
                        localRenderedDimensions?.height !== renderedDims.height
                    ) {
                        setLocalRenderedDimensions(renderedDims);
                    }
                });
            }
        }, [
            setVideoMetadataLoaded,
            localNaturalDimensions?.width,
            localNaturalDimensions?.height,
            setLocalNaturalDimensions,
            localRenderedDimensions?.width,
            localRenderedDimensions?.height,
            setLocalRenderedDimensions
        ]);

        const handleFramePreviewsReady = useCallback(() => {
            setAreFramePreviewsReady(true);
        }, [setAreFramePreviewsReady]);

        // Effect to pass video control props to parent when available
        useEffect(() => {
            if (setVideoControls) {
                if (fileUrl && videoMetadataLoaded && currentMedia?.file) {
                    setVideoControls({
                        videoRef: videoRef as React.RefObject<HTMLVideoElement>,
                        mediaItem: currentMedia?.file,
                        isPlaying,
                        onFramePreviewsReady: handleFramePreviewsReady,
                        onTimelineClick: flowLocked ? noop : handleTimelineClick,
                        onClockTick: handleClockTick,
                        onPauseClicked: flowLocked ? noop : onVideoPause
                    });
                } else {
                    setVideoControls(null);
                }
            }
        }, [
            setVideoControls,
            fileUrl,
            videoMetadataLoaded,
            videoRef,
            currentMedia?.file,
            isPlaying,
            handleFramePreviewsReady,
            flowLocked,
            handleTimelineClick,
            handleClockTick,
            onVideoPause
        ]);

        // Check inference status for LoadingOverlay
        const isInferenceInProgress = useIsGeneratingPredictions(currentMedia?.file.id || "");

        return (
            <>
                <video
                    ref={videoRef}
                    src={fileUrl ?? undefined}
                    crossOrigin="anonymous"
                    onError={(e) => {
                        console.warn("Error loading video from URL:", e);
                    }}
                    autoPlay={isReviewStep && !flowLocked}
                    muted={true}
                    loop={true}
                    playsInline={true}
                    webkit-playsinline="true"
                    controls={false}
                    className="absolute left-1/2 top-1/2 max-h-full w-auto max-w-full"
                    style={{
                        transform: `translate(-50%, -50%) scale(${effectiveZoomLevel}) translate(${effectiveZoomX}px, ${effectiveZoomY}px)`,
                        transformOrigin: "center center",
                        pointerEvents: flowLocked ? "none" : "auto"
                    }}
                    onLoadedMetadata={handleVisibleVideoMetadataLoaded}
                    onCanPlay={() => {
                        if (
                            videoMetadataLoaded &&
                            videoRef.current &&
                            videoRef.current.videoWidth > 0
                        ) {
                            handleVisibleVideoMetadataLoaded();
                        }
                    }}
                    preload="auto"
                />
                {fileCarouselHintPortalElement &&
                    createPortal(
                        <>
                            {step === BuildSteps.PROMPT && <AnnotateHint />}
                            {step === BuildSteps.PROMPT && <BackspaceHint />}
                            {isReviewStep && <DownloadingHint isVideo={true} />}
                            {/* Show inference progress hint for review, deploy (except improve), and share flows */}
                            {(isReviewStep ||
                                ((flow === Flows.DEPLOY || flow === Flows.SHARE) &&
                                    step !== DeploySteps.IMPROVE)) &&
                                currentMedia?.file && (
                                    <InferenceProgressHint fileId={currentMedia.file.id} />
                                )}

                            {/* {isReviewStep && <PredictionsLoopProgressHint />} */}
                        </>,
                        fileCarouselHintPortalElement
                    )}
            </>
        );
    }
);

VideoFilePreview.displayName = "VideoFilePreview";

export default VideoFilePreview;
